import React from 'react';
import { Card, Col, Row, Statistic } from 'antd';

export type StatCardItem = {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  suffix?: string;
  prefix?: React.ReactNode;
};

type StatCardGridProps = {
  stats: StatCardItem[];
  /**
   * Number of columns at the largest (lg+) breakpoint.
   * The grid scales down responsively:
   *   - xs: always 1 column (full width)
   *   - sm: 2 columns
   *   - md: 2 columns (or `lgColumns` if <= 2)
   *   - lg+: `lgColumns` columns
   */
  lgColumns?: 2 | 3 | 4 | 6;
  gutter?: [number, number];
};

/**
 * Responsive grid of stat cards. Use this for any page-level summary
 * metrics (totals, counts, aggregates).
 *
 * On mobile (xs), each card takes the full row to prevent label/value wrapping.
 * On tablet (sm/md), cards arrange 2-up. On desktop (lg+), cards use `lgColumns`.
 */
const StatCardGrid: React.FC<StatCardGridProps> = ({ stats, lgColumns = 4, gutter = [16, 16] }) => {
  if (!stats.length) return null;

  // Ant Design uses a 24-column grid
  const lgSpan = 24 / lgColumns;
  const mdSpan = lgColumns <= 2 ? lgSpan : 12;

  return (
    <Row gutter={gutter} className="stat-card-grid">
      {stats.map(({ title, value, icon, suffix, prefix }) => (
        <Col key={title} xs={24} sm={12} md={mdSpan} lg={lgSpan}>
          <Card>
            <Statistic title={title} value={value} prefix={prefix ?? icon} suffix={suffix} />
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default StatCardGrid;
